import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type {
  AnnouncementConfirmationParams,
  AnnouncementConfirmationResponse,
} from '~/services/api/community/getAnnouncementConfirmation.server';

export const ANNOUNCEMENT_CONFIRMATION_PATH = '/community/announcement_confirmation';

// Resolves to null when the author can't notify anyone (not an admin or manager, or the feature is off).
const useRequestAnnouncementConfirmation = () => {
  const queryClient = useQueryClient();

  return useCallback(
    ({ spaceSlug, everyone, badgeIds, postId }: AnnouncementConfirmationParams) =>
      queryClient.fetchQuery({
        queryKey: ['community-announcement-confirmation', spaceSlug, everyone, badgeIds, postId],
        // The count is the one at the moment of each submit.
        staleTime: 0,
        gcTime: 0,
        retry: false,
        queryFn: async ({ signal }): Promise<AnnouncementConfirmationResponse | null> => {
          const params = new URLSearchParams();
          if (spaceSlug) params.set('space_slug', spaceSlug);
          if (everyone) params.set('everyone', 'true');
          for (const badgeId of badgeIds) params.append('badge_ids[]', badgeId);
          if (postId) params.set('post_id', postId);

          const response = await fetch(`${ANNOUNCEMENT_CONFIRMATION_PATH}?${params}`, { signal });
          if (response.status === 403) return null;
          if (!response.ok) throw new Error('Failed to fetch the announcement confirmation');

          return response.json();
        },
      }),
    [queryClient],
  );
};

export default useRequestAnnouncementConfirmation;
